<template>
  <div>
    <NavBar />
    <main class="page dashboard-page">

      <section class="dashboard-hero">
        <div class="hero-copy">
          <span class="hero-kicker">{{ villeActiveLabel }}</span>
          <h1>Bonjour {{ commercant.nom_enseigne }}</h1>
          <p class="date">{{ dateAujourdhui }}</p>
        </div>
        <div class="hero-actions">
          <span class="live-badge" :class="{ connected: socketConnecte }" aria-live="polite">
            <span class="status-dot"></span>
            {{ socketConnecte ? 'Live' : 'Hors ligne' }}
          </span>
          <button class="btn btn-primary scan-btn" :disabled="encaissementBloque" @click="$router.push('/scanner')">
            <span class="btn-plus" aria-hidden="true"></span>
            Nouvelle vente
          </button>
        </div>
      </section>

      <div class="dashboard-alert error-alert" v-if="erreurDashboard">
        {{ erreurDashboard }}
      </div>

      <div class="dashboard-alert loading-alert" v-if="loadingDashboard">
        Synchronisation du tableau de bord...
      </div>

      <div class="dashboard-alert sync-alert" v-if="messageSynchronisation">
        {{ messageSynchronisation }}
      </div>

      <!-- Sélecteur de marché -->
      <div class="card marche-selector-card">
        <div class="marche-selector-row">
          <div class="marche-selector-label-wrap">
            <span class="market-icon" aria-hidden="true"></span>
            <div>
              <div class="marche-selector-label">Marché actif</div>
              <div class="marche-selector-value">{{ villeActiveLabel }}</div>
            </div>
          </div>
          <select v-model="villeActive" class="marche-select" @focus="memoriserVilleAvantSelection" @change="confirmerChangementMarche">
            <option v-for="m in optionsMarchesSelect" :key="m.marche_lieu_id || m.ville" :value="m.ville">
              {{ libelleMarche(m) }} — {{ m.heure_debut ? m.heure_debut.substring(0,5) : '07:00' }} → {{ m.heure_fin ? m.heure_fin.substring(0,5) : '13:00' }}
            </option>
            <option value="__autre__">Autre ville...</option>
          </select>
          <div class="marche-actif-badge" v-if="journeeActive && villeActive && villeActive !== '__autre__'">
            Actif
          </div>
        </div>

        <div class="ville-manuelle" v-if="villeActive === '__autre__'">
          <input v-model="villeManuelle" class="input" placeholder="Nom de la ville..." style="max-width:250px" />
          <button class="btn btn-primary" style="width:auto;padding:10px 16px" @click="validerVilleManuelle" :disabled="!villeManuelle">
            Confirmer
          </button>
        </div>

        <div class="marches-recap" v-if="tousMarches.length > 1">
          <div
            class="marche-recap-item"
            v-for="m in tousMarches"
            :key="m.marche_lieu_id || m.ville"
            :class="{ actif: m.ville === villeActive }"
            @click="selectionnerMarcheDepuisRecap(m)"
          >
            <div class="recap-ville">{{ libelleMarche(m) }}</div>
            <div class="recap-ca">{{ formatEuro(caMarche(m)) }}</div>
            <div class="recap-passages">{{ passagesMarcheLabel(m) }}</div>
          </div>
        </div>
      </div>

      <!-- 5 métriques -->
      <div class="metrics" v-if="!encaissementBloque && villeActive && villeActive !== '__autre__'">
        <div class="metric-card card" :class="{ flash: flashCA }">
          <div class="metric-topline">
            <span class="metric-icon icon-card"></span>
            <span class="metric-trend">Carte</span>
          </div>
          <div class="metric-value">{{ formatEuro(bord.ca_fidelise) }}</div>
          <div class="metric-label">CA fidélisé</div>
          <div class="metric-sub">{{ bord.nb_passages_carte }} passages carte</div>
        </div>
        <div class="metric-card card">
          <div class="metric-topline">
            <span class="metric-icon icon-receipt"></span>
            <span class="metric-trend">Direct</span>
          </div>
          <div class="metric-value">{{ formatEuro(bord.ca_sans_fidelite || 0) }}</div>
          <div class="metric-label">CA sans fidélité</div>
          <div class="metric-sub">Ventes sans carte</div>
        </div>
        <div class="metric-card card metric-highlight-blue">
          <div class="metric-topline">
            <span class="metric-icon icon-chart"></span>
            <span class="metric-trend">Total</span>
          </div>
          <div class="metric-value">{{ formatEuro(caTotal) }}</div>
          <div class="metric-label">CA total</div>
          <div class="metric-sub">Fidélisé + Sans carte</div>
        </div>
        <div class="metric-card card">
          <div class="metric-topline">
            <span class="metric-icon icon-expense"></span>
            <span class="metric-trend">Charges</span>
          </div>
          <div class="metric-value">{{ bord.total_depenses ? formatEuro(bord.total_depenses) : '—' }}</div>
          <div class="metric-label">Dépenses</div>
          <div class="metric-sub">Stock + place + carburant</div>
        </div>
        <div class="metric-card card" :class="{ 'metric-highlight': bord.marge_brute }">
          <div class="metric-topline">
            <span class="metric-icon icon-profit"></span>
            <span class="metric-trend">Marge</span>
          </div>
          <div class="metric-value">{{ bord.marge_brute ? formatEuro(bord.marge_brute) : '—' }}</div>
          <div class="metric-label">Bénéfice</div>
          <div class="metric-sub">{{ bord.marge_pct ? bord.marge_pct + '%' : 'Après clôture' }}</div>
        </div>
      </div>

      <div class="card no-marche" v-else>
        <span class="empty-icon" aria-hidden="true"></span>
        <p>{{ messageJourneeInactive }}</p>
      </div>

      <!-- Dernière transaction -->
      <div class="card derniere-tx" v-if="derniereTx">
        <div class="tx-icon" aria-hidden="true"></div>
        <div class="tx-info">
          <strong>Dernier achat</strong>
          <span>{{ formatEuro(derniereTx.montant_achat) }} — +{{ derniereTx.points_gagnes }} pts</span>
        </div>
        <div class="tx-time">{{ derniereTxTime }}</div>
      </div>

      <!-- Stats par heure -->
      <div class="card stats-heure-card" v-if="!encaissementBloque && villeActive && statsHeure.length > 0">
        <div class="stats-heure-header">
          <div>
            <span class="section-kicker">Performance terrain</span>
            <h2>Activité par heure</h2>
            <p>{{ villeActive }}</p>
          </div>
          <div class="stats-heure-total">
            <span>Total : <strong>{{ totalClientsHeure }} clients</strong></span>
            <span>CA : <strong>{{ formatEuro(totalCAHeure) }}</strong></span>
            <span v-if="totalClientsHeure > 0">Panier moy. : <strong>{{ formatEuro(totalCAHeure / totalClientsHeure) }}</strong></span>
          </div>
        </div>

        <!-- Tableau -->
        <div class="table-heure-wrap">
          <table class="table-heure">
            <thead>
              <tr>
                <th>Heure</th>
                <th>Nb. Clients</th>
                <th>CA</th>
                <th>Panier M.</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in statsHeure"
                :key="row.heure"
                :class="{ 'heure-peak': row.nb_clients == maxClients }"
              >
                <td class="heure-cell">{{ String(row.heure).padStart(2,'0') }}:00</td>
                <td>{{ row.nb_clients }}</td>
                <td>{{ formatEuro(row.ca) }}</td>
                <td>{{ formatEuro(row.panier_moyen) }}</td>
              </tr>
              <tr class="heure-total">
                <td><strong>Total</strong></td>
                <td><strong>{{ totalClientsHeure }}</strong></td>
                <td><strong>{{ formatEuro(totalCAHeure) }}</strong></td>
                <td><strong>{{ totalClientsHeure > 0 ? formatEuro(totalCAHeure / totalClientsHeure) : '—' }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Graphique barres CA -->
        <div class="charts-grid">
          <div class="graphique-wrap">
            <div class="graphique-titre">CA par heure</div>
            <div class="graphique-barres">
              <div
                class="barre-wrap"
                v-for="row in statsHeure"
                :key="'ca-' + row.heure"
              >
                <div class="barre-label-top">{{ formatEuroShort(row.ca) }}</div>
                <div class="barre-outer">
                  <div
                    class="barre-inner barre-ca"
                    :style="{ height: (parseFloat(row.ca) / maxCA * 100) + '%' }"
                  ></div>
                </div>
                <div class="barre-label-bot">{{ String(row.heure).padStart(2,'0') }}h</div>
              </div>
            </div>
          </div>

          <!-- Graphique barres clients -->
          <div class="graphique-wrap">
            <div class="graphique-titre">Clients par heure</div>
            <div class="graphique-barres">
              <div
                class="barre-wrap"
                v-for="row in statsHeure"
                :key="'cl-' + row.heure"
              >
                <div class="barre-label-top">{{ row.nb_clients }}</div>
                <div class="barre-outer">
                  <div
                    class="barre-inner barre-clients"
                    :style="{ height: (row.nb_clients / maxClients * 100) + '%' }"
                  ></div>
                </div>
                <div class="barre-label-bot">{{ String(row.heure).padStart(2,'0') }}h</div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div class="card empty-stats" v-else-if="!encaissementBloque && villeActive && villeActive !== '__autre__' && !loadingDashboard">
        <span class="empty-icon small" aria-hidden="true"></span>
        <div>
          <h3>Aucune activité horaire pour le moment</h3>
          <p>Les statistiques apparaîtront dès les premières ventes du marché.</p>
        </div>
      </div>

      <!-- Taux de fidélisation -->
      <div class="card loyalty-card" v-if="bord.taux_fidelisation_pct">
        <div class="loyalty-head">
          <div>
            <span class="section-kicker">Fidélité</span>
            <h2>Taux de fidélisation</h2>
          </div>
          <strong>{{ bord.taux_fidelisation_pct }}%</strong>
        </div>
        <div class="progress-bar-wrap">
          <div class="progress-bar" :style="{ width: bord.taux_fidelisation_pct + '%' }"></div>
        </div>
        <p class="progress-label">{{ bord.taux_fidelisation_pct }}% de votre CA vient de clients carte</p>
      </div>

    </main>

    <div v-if="popupSelectionMarche" class="market-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="market-modal-title">
      <div class="market-modal">
        <span class="market-modal-icon" aria-hidden="true"></span>
        <h2 id="market-modal-title">Sélectionnez le marché du jour</h2>
        <p v-if="toutesJourneesCloturees">
          Toutes les journées du jour sont clôturées. Vous ne pouvez plus encaisser sur ces marchés.
        </p>
        <p v-else>Avant d’encaisser, confirmez le marché sur lequel vous vendez aujourd’hui.</p>
        <p v-if="ancienneVilleLocale" class="market-modal-warning">
          Ancien marché actif détecté : {{ ancienneVilleLocale }}. Confirmez le marché du jour pour éviter les erreurs d’encaissement.
        </p>
        <div v-if="marchesPopup.length" class="market-modal-actions">
          <button
            v-for="marche in marchesPopup"
            :key="marche.marche_lieu_id || marche.journee_id || marche.id || marche.ville"
            type="button"
            class="btn btn-primary market-choice"
            :disabled="selectionMarcheLoading"
            @click="ouvrirMarcheConfirme(marche.ville, marche.marche_lieu_id)"
          >
            {{ libelleMarche(marche) }}
          </button>
          <button
            v-if="!tousMarches.length"
            type="button"
            class="btn btn-secondary market-choice"
            :disabled="selectionMarcheLoading"
            @click="villeActive = '__autre__'"
          >
            Autre marché
          </button>
        </div>
        <div v-else-if="toutesJourneesCloturees" class="market-modal-actions">
          <button type="button" class="btn btn-secondary market-choice" @click="$router.push('/journees')">
            Voir les journées
          </button>
          <button type="button" class="btn btn-secondary market-choice" @click="popupSelectionMarche = false">
            Retour au dashboard
          </button>
        </div>
        <div class="ville-manuelle modal-manual" v-if="villeActive === '__autre__'">
          <input v-model="villeManuelle" class="input" placeholder="Nom de la ville..." />
          <button class="btn btn-primary" @click="validerVilleManuelle" :disabled="!villeManuelle || selectionMarcheLoading">
            Confirmer
          </button>
        </div>
        <p v-if="erreurSelectionMarche" class="market-modal-error">{{ erreurSelectionMarche }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api    from '../api'
import { io } from 'socket.io-client'

const VILLE_KEY = 'marche_ville_active'
const ACTIVE_DAY_KEY = 'journee_active_confirmed'
const VILLES_CONNNUES = ['Sète', 'Mèze', 'La Grande Motte', 'Pézenas']
const MESSAGE_AUCUNE_JOURNEE = 'Aucune journée active confirmée. Sélectionnez le marché du jour avant d’encaisser.'

function normalizeVille(ville) {
  return String(ville || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’‘`´]/g, "'")
    .replace(/[-‐‑‒–—―]/g, ' ')
    .replace(/\s+/g, ' ')
}

function villesEquivalentes(villeA, villeB) {
  return normalizeVille(villeA) === normalizeVille(villeB)
}

export default {
  name: 'DashboardView',
  components: { NavBar },
  data() {
    return {
      commercant:    JSON.parse(localStorage.getItem('commercant') || '{}'),
      villeActive:   '',
      ancienneVilleLocale: localStorage.getItem(VILLE_KEY) || '',
      villeManuelle: '',
      marchesDuJour: [],
      tousMarches:   [],
      journeeActive: null,
      villeAvantSelection: '',
      popupSelectionMarche: false,
      selectionMarcheLoading: false,
      erreurSelectionMarche: '',
      messageSynchronisation: '',
      bord: {
        ca_fidelise: 0, nb_passages_carte: 0,
        ca_total: null, total_depenses: 0,
        marge_brute: null, marge_pct: null,
        taux_fidelisation_pct: null
      },
      statsHeure:     [],
      socket:         null,
      socketConnecte: false,
      derniereTx:     null,
      derniereTxTime: '',
      flashCA:        false,
      loadingDashboard: false,
      erreurDashboard:  '',
    }
  },
  computed: {
    dateAujourdhui() {
      return new Date().toLocaleDateString('fr-FR', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      })
    },
    villeActiveLabel() {
      if (this.journeeActive?.marche_lieu?.nom_affichage) return this.journeeActive.marche_lieu.nom_affichage
      const marche = this.marcheDepuisVille(this.villeActive)
      if (marche) return this.libelleMarche(marche)
      if (this.villeActive && this.villeActive !== '__autre__') return this.villeActive
      return 'Aucun marché sélectionné'
    },
    encaissementBloque() {
      return !this.journeeActive || this.journeeActive.statut === 'cloture'
    },
    messageJourneeInactive() {
      if (this.erreurDashboard) return this.erreurDashboard
      return MESSAGE_AUCUNE_JOURNEE
    },
    optionsMarchesSelect() {
      const map = new Map()
      this.marchesDuJour.forEach(m => {
        if (m?.ville) map.set(m.ville, m)
      })
      if (this.journeeActive?.ville && !map.has(this.journeeActive.ville)) {
        map.set(this.journeeActive.ville, {
          ville: this.journeeActive.ville,
          heure_debut: null,
          heure_fin: null,
          marche_lieu_id: this.journeeActive.marche_lieu_id || null,
          marche_lieu: this.journeeActive.marche_lieu || null
        })
      }
      return Array.from(map.values())
    },
    marchesPopup() {
      const ouvertes = this.journeesOuvertesDuJour()
      const source = ouvertes.length
        ? ouvertes
        : (this.tousMarches.length ? [] : (this.marchesDuJour.length ? this.marchesDuJour : VILLES_CONNNUES.map(ville => ({ ville }))))
      const map = new Map()
      source.forEach(m => {
        if (m?.ville) map.set(m.marche_lieu_id || m.journee_id || m.id || m.ville, m)
      })
      return Array.from(map.values())
    },
    toutesJourneesCloturees() {
      return this.tousMarches.length > 0
        && this.journeesOuvertesDuJour().length === 0
        && this.tousMarches.some(j => j?.ville && (j.statut || 'en_cours') === 'cloture')
    },
    caTotal() {
      return (parseFloat(this.bord.ca_fidelise) || 0) + (parseFloat(this.bord.ca_sans_fidelite) || 0)
    },
    maxCA() {
      return Math.max(...this.statsHeure.map(r => parseFloat(r.ca || 0)), 1)
    },
    maxClients() {
      return Math.max(...this.statsHeure.map(r => parseInt(r.nb_clients || 0)), 1)
    },
    totalCAHeure() {
      return this.statsHeure.reduce((s, r) => s + parseFloat(r.ca || 0), 0)
    },
    totalClientsHeure() {
      return this.statsHeure.reduce((s, r) => s + parseInt(r.nb_clients || 0), 0)
    }
  },
  methods: {
    nombre(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },
    caMarche(m) {
      if (m.ca_total_saisi !== null && m.ca_total_saisi !== undefined) return this.nombre(m.ca_total_saisi)
      if (m.ca_total !== null && m.ca_total !== undefined) return this.nombre(m.ca_total)

      const caFidelise = this.nombre(m.ca_fidelise)
      if (m.ca_sans_fidelite !== null && m.ca_sans_fidelite !== undefined) {
        return caFidelise + this.nombre(m.ca_sans_fidelite)
      }
      if (m.ca_sans_carte !== null && m.ca_sans_carte !== undefined) {
        return caFidelise + this.nombre(m.ca_sans_carte)
      }
      return 0
    },
    passagesMarcheLabel(m) {
      if (m.passages !== null && m.passages !== undefined) return `${this.nombre(m.passages)} passages`
      if (m.nb_passages !== null && m.nb_passages !== undefined) return `${this.nombre(m.nb_passages)} passages`
      return `${this.nombre(m.nb_passages_carte)} passages carte`
    },
    libelleMarche(m) {
      return m?.marche_lieu?.nom_affichage || m?.nom_affichage || m?.ville || ''
    },
    normaliserVille(ville) {
      return normalizeVille(ville)
    },
    villesEquivalentes(villeA, villeB) {
      return villesEquivalentes(villeA, villeB)
    },
    marcheDepuisVille(ville) {
      if (!ville || ville === '__autre__') return null
      return this.marchesDuJour.find(m => this.villesEquivalentes(m.ville, ville)) || null
    },
    payloadMarche(ville, marcheLieuId) {
      const marche = marcheLieuId ? null : this.marcheDepuisVille(ville)
      const id = marcheLieuId || marche?.marche_lieu_id || null
      return id ? { ville, marche_lieu_id: id } : { ville }
    },
    formatEuro(v) {
      return Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
    },
    formatEuroShort(v) {
      const n = Number(v || 0)
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k€'
      return n.toFixed(0) + '€'
    },
    todayIso() {
      return new Date().toISOString().split('T')[0]
    },
    confirmerJourneeActive(journee) {
      if (!journee?.id) return
      localStorage.setItem(ACTIVE_DAY_KEY, JSON.stringify({
        active: true,
        date: this.todayIso(),
        journee_id: journee.id,
        marche_lieu_id: journee.marche_lieu_id || '',
        nom_affichage: journee.marche_lieu?.nom_affichage || '',
        code_postal: journee.marche_lieu?.code_postal || '',
        code_insee: journee.marche_lieu?.code_insee || '',
        ville: journee.ville || '',
        confirmedAt: new Date().toISOString()
      }))
    },
    synchroniserJourneeActive(journee) {
      this.journeeActive = journee
      this.villeActive = journee.ville || ''
      this.popupSelectionMarche = false
      this.erreurSelectionMarche = ''
      localStorage.setItem(VILLE_KEY, this.villeActive)
      this.confirmerJourneeActive(journee)
      if (this.ancienneVilleLocale && this.ancienneVilleLocale !== this.villeActive) {
        this.messageSynchronisation = 'Marché synchronisé avec la journée active.'
      }
      this.ancienneVilleLocale = this.villeActive
    },
    async chargerJourneeActive(params = {}) {
      const res = await api.get('/journee/active', { params })
      if (res.data?.active && res.data?.journee) {
        this.synchroniserJourneeActive(res.data.journee)
        return res.data.journee
      }
      throw new Error(res.data?.error || MESSAGE_AUCUNE_JOURNEE)
    },
    journeesOuvertesDuJour() {
      return (Array.isArray(this.tousMarches) ? this.tousMarches : [])
        .filter(j => j?.ville && (j.statut || 'en_cours') !== 'cloture')
    },
    journeesClotureesDuJour() {
      return (Array.isArray(this.tousMarches) ? this.tousMarches : [])
        .filter(j => j?.ville && (j.statut || 'en_cours') === 'cloture')
    },
    journeeClotureePourVille(ville) {
      const villeNormalisee = this.normaliserVille(ville)
      if (!villeNormalisee) return null

      const matches = this.journeesClotureesDuJour()
        .filter(j => this.normaliserVille(j.ville) === villeNormalisee)

      return matches.length === 1 ? matches[0] : null
    },
    marchePlanifiePourJournee(journee) {
      if (!journee?.ville) return null
      return this.marchesDuJour.find(m => {
        if (!this.villesEquivalentes(m.ville, journee.ville)) return false
        if (journee.marche_lieu_id && m.marche_lieu_id) return String(m.marche_lieu_id) === String(journee.marche_lieu_id)
        return true
      }) || null
    },
    journeeOuvertePourVille(ville) {
      const villeNormalisee = this.normaliserVille(ville)
      if (!villeNormalisee) return null

      const matches = this.journeesOuvertesDuJour()
        .filter(j => this.normaliserVille(j.ville) === villeNormalisee)

      return matches.length === 1 ? matches[0] : null
    },
    paramsJourneeActivePourJournee(journee) {
      const marche = this.marchePlanifiePourJournee(journee)
      const params = { ville: journee.ville }
      if (journee.marche_lieu_id) params.marche_lieu_id = journee.marche_lieu_id
      else if (marche?.marche_lieu_id) params.marche_lieu_id = marche.marche_lieu_id
      return params
    },
    async chargerJourneeActiveLocale() {
      const villeLocale = localStorage.getItem(VILLE_KEY) || this.ancienneVilleLocale || ''
      const journeeLocale = this.journeeOuvertePourVille(villeLocale)
      if (!journeeLocale) return null

      return this.chargerJourneeActive(this.paramsJourneeActivePourJournee(journeeLocale))
    },

    async chargerMarchesDuJour() {
      this.loadingDashboard = true
      this.erreurDashboard = ''
      try {
        const [planifies, journees] = await Promise.all([
          api.get('/journee/marches-today'),
          api.get('/journee/today')
        ])
        this.marchesDuJour = planifies.data
        this.tousMarches   = journees.data

        if (this.journeeActive && this.villeActive) {
          await this.chargerBord()
          await this.chargerStatsHeure()
        }
      } catch (err) {
        console.error(err)
        this.erreurDashboard = 'Impossible de synchroniser les données du tableau de bord.'
      } finally {
        this.loadingDashboard = false
      }
    },

    memoriserVilleAvantSelection() {
      this.villeAvantSelection = this.villeActive
    },

    async confirmerChangementMarche() {
      if (!this.villeActive || this.villeActive === '__autre__') return
      const nouvelleVille = this.villeActive
      const ancienneVille = this.villeAvantSelection || this.journeeActive?.ville || ''
      if (ancienneVille && ancienneVille !== nouvelleVille) {
        const ok = window.confirm(`Changer le marché actif de "${ancienneVille}" vers "${nouvelleVille}" ?`)
        if (!ok) {
          this.villeActive = ancienneVille
          return
        }
      }
      await this.ouvrirMarcheConfirme(nouvelleVille)
    },

    async selectionnerMarcheDepuisRecap(marche) {
      const ville = marche?.ville
      if (!ville) return
      this.villeAvantSelection = this.villeActive
      this.villeActive = ville
      await this.ouvrirMarcheConfirme(ville, marche.marche_lieu_id)
    },

    async ouvrirMarcheConfirme(ville, marcheLieuId = null) {
      if (!ville || this.selectionMarcheLoading) return
      this.selectionMarcheLoading = true
      this.erreurSelectionMarche = ''
      try {
        const payload = this.payloadMarche(ville, marcheLieuId)
        await api.post('/journee/select-marche', payload)
        await this.chargerJourneeActive(payload)
        await this.chargerMarchesDuJour()
        await this.chargerBord()
        await this.chargerStatsHeure()
      } catch (err) {
        this.erreurSelectionMarche = err.response?.data?.error || "Impossible de confirmer le marché du jour."
        this.erreurDashboard = this.erreurSelectionMarche
      } finally {
        this.selectionMarcheLoading = false
      }
    },

    async validerVilleManuelle() {
      if (!this.villeManuelle) return
      const ville = this.villeManuelle.trim()
      if (!ville) return
      this.villeActive   = ville
      this.villeManuelle = ''
      await this.ouvrirMarcheConfirme(ville)
    },

    async chargerBord() {
      if (!this.villeActive || this.villeActive === '__autre__') return
      try {
        const res  = await api.get('/journee/today/' + encodeURIComponent(this.villeActive))
        this.bord  = res.data
      } catch {
        this.erreurDashboard = 'Impossible de charger les indicateurs du marché actif.'
      }
    },

    async chargerStatsHeure() {
      if (!this.villeActive || this.villeActive === '__autre__') return
      try {
        const res       = await api.get('/journee/stats-heure?ville=' + encodeURIComponent(this.villeActive))
        this.statsHeure = res.data
      } catch {
        this.erreurDashboard = 'Impossible de charger les statistiques horaires.'
      }
    },

    connecterSocket() {
      const apiUrl = process.env.VUE_APP_API_URL || 'http://91.98.17.62:3000'
      this.socket  = io(apiUrl, { transports: ['websocket'] })

      this.socket.on('connect',    () => { this.socketConnecte = true })
      this.socket.on('disconnect', () => { this.socketConnecte = false })

      this.socket.on('transaction', (data) => {
        if (data.commercant_id !== this.commercant.id) return
        if (data.ville && data.ville !== this.villeActive) return

        this.bord.ca_fidelise       = parseFloat(this.bord.ca_fidelise) + parseFloat(data.montant_achat)
        this.bord.nb_passages_carte = parseInt(this.bord.nb_passages_carte) + 1
        this.derniereTx             = data
        this.derniereTxTime         = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
        this.flashCA                = true
        setTimeout(() => this.flashCA = false, 1000)
        this.chargerStatsHeure()
        this.initialiserDashboard()
      })
    },

    async initialiserDashboard() {
      this.loadingDashboard = true
      this.erreurDashboard = ''
      try {
        await this.chargerMarchesDuJour()
        const villeLocale = localStorage.getItem(VILLE_KEY) || this.ancienneVilleLocale || ''
        if (this.journeeClotureePourVille(villeLocale)) {
          this.journeeActive = null
          this.villeActive = ''
          localStorage.removeItem(VILLE_KEY)
          localStorage.removeItem(ACTIVE_DAY_KEY)
          this.erreurDashboard = 'Le marché mémorisé est clôturé. Sélectionnez une journée ouverte.'
          this.erreurSelectionMarche = this.erreurDashboard
          this.popupSelectionMarche = true
          return
        }
        const journeeLocale = await this.chargerJourneeActiveLocale()
        if (!journeeLocale) await this.chargerJourneeActive()
        await this.chargerBord()
        await this.chargerStatsHeure()
      } catch (err) {
        this.journeeActive = null
        this.villeActive = ''
        localStorage.removeItem(ACTIVE_DAY_KEY)
        this.erreurDashboard = this.toutesJourneesCloturees
          ? 'Toutes les journées du jour sont clôturées. Vous ne pouvez plus encaisser sur ces marchés.'
          : (err.response?.data?.error || MESSAGE_AUCUNE_JOURNEE)
        this.popupSelectionMarche = true
      } finally {
        this.loadingDashboard = false
      }
    }
  },
  mounted() {
    this.initialiserDashboard()
    this.connecterSocket()
    this.interval = setInterval(() => {
      this.chargerBord()
      this.chargerStatsHeure()
    }, 60000)
  },
  unmounted() {
    clearInterval(this.interval)
    if (this.socket) this.socket.disconnect()
  }
}
</script>

<style scoped>
.dashboard-page {
  max-width: 1180px;
  padding: 32px 28px 40px;
}

.dashboard-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 22px;
  padding: 28px;
  margin-bottom: 18px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: var(--fm-card-radius);
  background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 58%, #EEF6FF 100%);
  box-shadow: var(--fm-shadow-soft);
}

.hero-copy { min-width: 0; }
.hero-kicker,
.section-kicker {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  margin-bottom: 8px;
  color: var(--fm-accent);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
}
.dashboard-hero h1 {
  color: var(--fm-navy);
  font-size: 30px;
  font-weight: 800;
  letter-spacing: 0;
}
.date {
  color: var(--fm-muted);
  font-size: 14px;
  margin-top: 8px;
}
.hero-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 8px 13px;
  border-radius: 999px;
  border: 1px solid #FECACA;
  background: var(--fm-danger-soft);
  color: #B91C1C;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}
.live-badge.connected {
  border-color: #BBF7D0;
  background: var(--fm-success-soft);
  color: #166534;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
}
.connected .status-dot { box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.14); }
.scan-btn {
  width: auto;
  min-height: 42px;
  padding: 10px 17px;
  white-space: nowrap;
}
.btn-plus {
  position: relative;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
}
.btn-plus::before,
.btn-plus::after {
  content: '';
  position: absolute;
  background: currentColor;
  border-radius: 999px;
}
.btn-plus::before { left: 2px; right: 2px; top: 7px; height: 2px; }
.btn-plus::after { top: 2px; bottom: 2px; left: 7px; width: 2px; }

.dashboard-alert {
  margin-bottom: 14px;
  padding: 13px 16px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
}
.error-alert {
  color: #B91C1C;
  background: var(--fm-danger-soft);
  border: 1px solid #FECACA;
}
.loading-alert {
  color: #1D4ED8;
  background: var(--fm-accent-soft);
  border: 1px solid #BFDBFE;
}
.sync-alert {
  color: #166534;
  background: var(--fm-success-soft);
  border: 1px solid #BBF7D0;
}
.scan-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.marche-selector-card {
  padding: 18px;
  margin-bottom: 18px;
}
.marche-selector-row {
  display: grid;
  grid-template-columns: minmax(190px, 1fr) minmax(260px, 420px) auto;
  align-items: center;
  gap: 14px;
}
.marche-selector-label-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.market-icon,
.metric-icon,
.tx-icon,
.empty-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}
.market-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: var(--fm-accent-soft);
}
.market-icon::before {
  content: '';
  width: 14px;
  height: 14px;
  border: 3px solid var(--fm-accent);
  border-radius: 999px 999px 999px 2px;
  transform: rotate(-45deg);
}
.market-icon::after {
  content: '';
  position: absolute;
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: var(--fm-accent);
}
.marche-selector-label {
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
}
.marche-selector-value {
  color: var(--fm-text);
  font-size: 17px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.marche-select {
  width: 100%;
  min-height: 48px;
  padding: 12px 42px 12px 14px;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  color: var(--fm-text);
  background: var(--fm-surface);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  outline: none;
  transition: border-color var(--fm-transition), box-shadow var(--fm-transition), background var(--fm-transition);
}
.marche-select:focus {
  border-color: var(--fm-accent);
  box-shadow: 0 0 0 4px rgba(30, 111, 220, 0.12);
}
.marche-actif-badge {
  justify-self: end;
  padding: 8px 13px;
  border-radius: 999px;
  border: 1px solid #BBF7D0;
  background: var(--fm-success-soft);
  color: #166534;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}
.ville-manuelle {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 14px;
  flex-wrap: wrap;
}
.marches-recap {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--fm-border);
}
.marche-recap-item {
  min-width: 0;
  padding: 13px 14px;
  border-radius: 14px;
  border: 1px solid var(--fm-border);
  background: #F8FAFC;
  cursor: pointer;
  transition: transform var(--fm-transition), border-color var(--fm-transition), background var(--fm-transition), box-shadow var(--fm-transition);
}
.marche-recap-item:hover {
  transform: translateY(-1px);
  border-color: #BFDBFE;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
}
.marche-recap-item.actif {
  border-color: rgba(30, 111, 220, 0.42);
  background: var(--fm-accent-soft);
}
.recap-ville {
  color: var(--fm-text);
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.recap-ca {
  color: var(--fm-navy);
  font-size: 19px;
  font-weight: 800;
}
.recap-passages {
  color: var(--fm-muted);
  font-size: 12px;
  margin-top: 2px;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}
.metric-card {
  position: relative;
  overflow: hidden;
  min-height: 174px;
  padding: 18px;
  margin-bottom: 0;
  transition: transform var(--fm-transition), box-shadow var(--fm-transition), border-color var(--fm-transition);
}
.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--fm-shadow-soft);
}
.metric-card.metric-highlight,
.metric-highlight-blue {
  border-color: rgba(30, 111, 220, 0.38);
}
.metric-card.metric-highlight { border-color: rgba(34, 197, 94, 0.44); }
.metric-card.flash { animation: flash 0.55s ease; }
@keyframes flash {
  0% { background: var(--fm-success-soft); transform: scale(1.015); }
  100% { background: var(--fm-surface); transform: scale(1); }
}
.metric-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 18px;
}
.metric-icon {
  width: 38px;
  height: 38px;
  border-radius: 13px;
  background: var(--fm-accent-soft);
}
.metric-icon::before,
.metric-icon::after {
  content: '';
  position: absolute;
  box-sizing: border-box;
}
.icon-card::before { width: 18px; height: 13px; border: 2px solid var(--fm-accent); border-radius: 4px; }
.icon-card::after { width: 12px; height: 2px; background: var(--fm-accent); transform: translateY(-2px); }
.icon-receipt::before { width: 15px; height: 18px; border: 2px solid var(--fm-accent); border-radius: 4px; }
.icon-receipt::after { width: 9px; height: 2px; background: var(--fm-accent); box-shadow: 0 5px 0 var(--fm-accent); }
.icon-chart::before { width: 18px; height: 18px; border-left: 2px solid var(--fm-accent); border-bottom: 2px solid var(--fm-accent); }
.icon-chart::after { width: 15px; height: 9px; border-top: 3px solid var(--fm-accent); border-right: 3px solid var(--fm-accent); transform: rotate(-18deg); }
.icon-expense::before { width: 18px; height: 18px; border-radius: 999px; border: 2px solid #94A3B8; }
.icon-expense::after { width: 12px; height: 2px; background: #94A3B8; transform: rotate(45deg); }
.icon-profit {
  background: var(--fm-success-soft);
}
.icon-profit::before { width: 18px; height: 18px; border-radius: 999px; border: 2px solid var(--fm-success); }
.icon-profit::after { width: 10px; height: 6px; border-left: 2px solid var(--fm-success); border-bottom: 2px solid var(--fm-success); transform: rotate(-45deg); }
.metric-trend {
  color: var(--fm-muted);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
}
.metric-value {
  color: var(--fm-navy);
  font-size: 25px;
  font-weight: 800;
  line-height: 1.12;
  overflow-wrap: anywhere;
}
.metric-label {
  color: var(--fm-text);
  font-size: 13px;
  font-weight: 800;
  margin-top: 10px;
}
.metric-sub {
  color: var(--fm-muted);
  font-size: 12px;
  margin-top: 4px;
}

.no-marche,
.empty-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 28px;
}
.no-marche p,
.empty-stats p {
  margin: 0;
}
.empty-stats {
  justify-content: flex-start;
}
.empty-stats h3 {
  color: var(--fm-text);
  font-size: 16px;
  margin-bottom: 4px;
}
.empty-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: var(--fm-surface-soft);
}
.empty-icon.small {
  width: 38px;
  height: 38px;
}
.empty-icon::before {
  content: '';
  width: 18px;
  height: 12px;
  border-left: 2px solid var(--fm-muted);
  border-bottom: 2px solid var(--fm-muted);
}

.derniere-tx {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px 18px;
  margin-bottom: 18px;
  border-color: #BBF7D0;
  background: linear-gradient(135deg, #F0FDF4 0%, #FFFFFF 100%);
}
.tx-icon {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: var(--fm-success-soft);
}
.tx-icon::before {
  content: '';
  width: 16px;
  height: 19px;
  border: 2px solid var(--fm-success);
  border-radius: 4px;
}
.tx-icon::after {
  content: '';
  position: absolute;
  width: 9px;
  height: 2px;
  background: var(--fm-success);
  box-shadow: 0 5px 0 var(--fm-success);
}
.tx-info {
  flex: 1;
  min-width: 0;
}
.tx-info strong {
  display: block;
  color: var(--fm-text);
  font-size: 14px;
  font-weight: 800;
}
.tx-info span {
  color: #15803D;
  font-size: 13px;
  font-weight: 700;
}
.tx-time {
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.stats-heure-card {
  padding: 22px;
  margin-bottom: 18px;
}
.stats-heure-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.stats-heure-header h2,
.loyalty-head h2 {
  color: var(--fm-navy);
  font-size: 20px;
  font-weight: 800;
}
.stats-heure-header p {
  font-size: 13px;
  margin-top: 4px;
}
.stats-heure-total {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.stats-heure-total span {
  padding: 8px 10px;
  border-radius: 999px;
  background: #F8FAFC;
  border: 1px solid var(--fm-border);
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
}
.stats-heure-total strong {
  color: var(--fm-text);
}

.table-heure-wrap {
  overflow-x: auto;
  margin-bottom: 20px;
  border: 1px solid var(--fm-border);
  border-radius: 16px;
}
.table-heure {
  width: 100%;
  min-width: 520px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
}
.table-heure th {
  padding: 12px 14px;
  text-align: left;
  color: var(--fm-muted);
  background: #F8FAFC;
  border-bottom: 1px solid var(--fm-border);
  font-size: 12px;
  font-weight: 800;
}
.table-heure td {
  padding: 13px 14px;
  border-bottom: 1px solid var(--fm-border);
  color: var(--fm-text);
}
.table-heure tbody tr:last-child td { border-bottom: 0; }
.table-heure tr:hover td { background: #F8FAFC; }
.heure-cell {
  color: var(--fm-navy);
  font-weight: 800;
}
.heure-peak td {
  background: #EFF6FF !important;
}
.heure-total td {
  background: #F8FAFC;
  border-top: 1px solid var(--fm-border);
  font-weight: 800;
  color: var(--fm-text);
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.graphique-wrap {
  min-width: 0;
  padding: 16px;
  border: 1px solid var(--fm-border);
  border-radius: 16px;
  background: #F8FAFC;
}
.graphique-titre {
  color: var(--fm-text);
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 12px;
}
.graphique-barres {
  display: flex;
  align-items: flex-end;
  gap: 7px;
  height: 148px;
  padding-bottom: 24px;
  position: relative;
}
.barre-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  min-width: 24px;
}
.barre-label-top {
  color: var(--fm-muted);
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 5px;
  height: 14px;
  white-space: nowrap;
}
.barre-outer {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: flex-end;
  background: #E2E8F0;
  border-radius: 999px 999px 6px 6px;
  overflow: hidden;
}
.barre-inner {
  width: 100%;
  min-height: 4px;
  border-radius: 999px 999px 6px 6px;
  transition: height 0.4s ease;
}
.barre-ca { background: linear-gradient(180deg, #3B82F6 0%, var(--fm-accent) 100%); }
.barre-clients { background: linear-gradient(180deg, #34D399 0%, var(--fm-success) 100%); }
.barre-label-bot {
  color: var(--fm-muted);
  font-size: 11px;
  font-weight: 700;
  margin-top: 5px;
}

.loyalty-card {
  padding: 22px;
}

.market-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.55);
}
.market-modal {
  width: min(100%, 520px);
  padding: 24px;
  border-radius: var(--fm-card-radius);
  background: #FFFFFF;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
}
.market-modal-icon {
  display: block;
  width: 42px;
  height: 42px;
  margin-bottom: 14px;
  border-radius: 12px;
  background: var(--fm-accent-soft);
  position: relative;
}
.market-modal-icon::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 9px;
  width: 14px;
  height: 20px;
  border: 3px solid var(--fm-primary);
  border-radius: 999px 999px 999px 0;
  transform: rotate(-45deg);
}
.market-modal h2 {
  margin: 0 0 8px;
  font-size: 24px;
  color: var(--fm-ink);
}
.market-modal p {
  margin: 0 0 14px;
  color: var(--fm-muted);
  line-height: 1.45;
}
.market-modal-warning,
.market-modal-error {
  padding: 12px 14px;
  border-radius: 12px;
  font-weight: 700;
}
.market-modal-warning {
  color: #92400E;
  background: #FEF3C7;
  border: 1px solid #FDE68A;
}
.market-modal-error {
  color: #B91C1C;
  background: var(--fm-danger-soft);
  border: 1px solid #FECACA;
}
.market-modal-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
}
.market-choice {
  min-height: 44px;
  justify-content: center;
}
.modal-manual {
  margin-top: 14px;
}
.modal-manual .input {
  flex: 1;
  max-width: none !important;
}
.loyalty-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.loyalty-head strong {
  color: var(--fm-success);
  font-size: 24px;
  font-weight: 800;
}
.progress-bar-wrap {
  background: #E2E8F0;
  border-radius: 999px;
  height: 12px;
  margin: 16px 0 8px;
  overflow: hidden;
}
.progress-bar {
  background: linear-gradient(90deg, var(--fm-accent) 0%, var(--fm-success) 100%);
  height: 100%;
  border-radius: 999px;
  transition: width 0.5s ease;
}
.progress-label {
  color: var(--fm-muted);
  font-size: 13px;
}

@media (max-width: 1100px) {
  .metrics { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 900px) {
  .dashboard-page { padding: 24px 18px 110px; }
  .dashboard-hero { flex-direction: column; }
  .hero-actions { width: 100%; justify-content: space-between; }
  .marche-selector-row { grid-template-columns: 1fr; }
  .marche-actif-badge { justify-self: start; }
  .metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .charts-grid { grid-template-columns: 1fr; }
  .stats-heure-total { justify-content: flex-start; }
}

@media (max-width: 600px) {
  .dashboard-page { padding: 14px 12px 104px; }
  .dashboard-hero {
    padding: 20px;
    margin-bottom: 14px;
  }
  .dashboard-hero h1 { font-size: 23px; }
  .hero-actions { align-items: stretch; }
  .live-badge { justify-content: center; }
  .scan-btn { width: 100%; }
  .marche-selector-card,
  .stats-heure-card,
  .loyalty-card { padding: 16px; }
  .metrics {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 2px 2px 8px;
    scroll-snap-type: x mandatory;
  }
  .metric-card {
    min-width: 76%;
    scroll-snap-align: start;
  }
  .metric-value { font-size: 23px; }
  .ville-manuelle .input { max-width: none !important; }
  .ville-manuelle .btn { width: 100% !important; }
  .market-modal-overlay {
    align-items: flex-end;
    padding: 10px;
  }
  .market-modal {
    padding: 20px;
  }
  .market-modal-actions {
    grid-template-columns: 1fr;
  }
  .derniere-tx {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .tx-time { margin-left: 55px; }
  .stats-heure-total {
    flex-direction: column;
    width: 100%;
  }
  .stats-heure-total span { width: 100%; }
  .graphique-wrap { padding: 14px 12px; }
  .graphique-barres {
    gap: 5px;
    overflow-x: auto;
  }
  .barre-wrap {
    min-width: 34px;
  }
  .no-marche,
  .empty-stats {
    align-items: flex-start;
    justify-content: flex-start;
  }
}
</style>
